import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { toast } from 'react-toastify'

import {
    useApplicationWorkbookTemplate,
    useCreateEzosWorkbookField,
    useEzosWorkbookFields,
    useSaveApplicationWorkbookTemplate,
    useUpdateEzosWorkbookField,
} from '../../ApplicationWorkbook/useApplicationWorkbookApi'
import {
    definitionToSectionsState,
    sectionsStateToDefinition,
} from '../../ApplicationWorkbook/workbookDefinitionMap'
import {
    STANDARD_WORKBOOK_TEMPLATE_KEY,
    type EzosWorkbookField,
} from '../../ApplicationWorkbook/workbook.types'
import { ApplicantDataTab } from '../../LeasingSeasonConfig/ApplicantDataTab'
import type { WorkbookSectionsState } from '../../PipelineFlowDemo/workbookSectionDraft'
import type {
    FieldIdCollision,
    FieldRegistryAdapter,
    OrgWorkbookField,
} from '../../PipelineFlowDemo/orgFieldRegistry'
import { suggestFieldId as localSuggest } from '../../PipelineFlowDemo/orgFieldRegistry'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5, 1.5),
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        flexShrink: 0,
    },
    title: {
        fontWeight: 700,
        fontSize: '1.05rem',
    },
    meta: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
    },
    column: {
        width: '100%',
        maxWidth: 600,
    },
    centered: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        gap: theme.spacing(1),
    },
}))

function toOrgField(row: EzosWorkbookField): OrgWorkbookField {
    return {
        field_id: row.field_id,
        question_text: row.question_text,
        input_type: row.input_type,
        options: row.options?.length ? [...row.options] : undefined,
        source: 'ezos_catalog',
        pinned: Boolean(row.pinned),
        usedIn: row.default_section_id
            ? [`EZOS · ${row.default_section_id}`]
            : ['EZOS registry'],
    }
}

/**
 * EZOS admin editor for the single standard Applicant Data workbook template.
 * No chooser — loads ``standard_student`` and persists placements + registry creates.
 */
export const EzosApplicantDataTemplateEditor: React.FC = () => {
    const classes = useStyles()
    const templateQuery = useApplicationWorkbookTemplate(
        STANDARD_WORKBOOK_TEMPLATE_KEY,
    )
    const fieldsQuery = useEzosWorkbookFields(false)
    const saveTemplate = useSaveApplicationWorkbookTemplate(
        STANDARD_WORKBOOK_TEMPLATE_KEY,
    )
    const createField = useCreateEzosWorkbookField()
    const updateField = useUpdateEzosWorkbookField()

    const [sections, setSections] = useState<WorkbookSectionsState | undefined>()
    const [dirty, setDirty] = useState(false)

    useEffect(() => {
        if (!templateQuery.data || !fieldsQuery.data) return
        setSections(
            definitionToSectionsState(
                templateQuery.data.definition,
                fieldsQuery.data,
            ),
        )
        setDirty(false)
    }, [templateQuery.data, fieldsQuery.data])

    const registry: FieldRegistryAdapter = useMemo(() => {
        const rows = fieldsQuery.data || []
        const map = new Map(rows.map((r) => [r.field_id, r]))

        return {
            listFields: () =>
                rows.filter((r) => r.is_active).map(toOrgField),
            checkCollision: (fieldId: string): FieldIdCollision | null => {
                const existing = map.get(fieldId.trim())
                if (!existing) return null
                return {
                    field_id: existing.field_id,
                    existing: toOrgField(existing),
                }
            },
            suggestFieldId: localSuggest,
            suggestAlternativeFieldId: (preferred, question) => {
                const extra = localSuggest(question)
                    .split('_')
                    .filter((t) => t && !preferred.split('_').includes(t))
                    .slice(0, 3)
                    .join('_')
                let candidate = extra
                    ? `${preferred}_${extra}`
                    : `${preferred}_2`
                let n = 2
                while (map.has(candidate)) {
                    const suffix = `_${n}`
                    candidate = `${preferred.slice(0, 56 - suffix.length)}${suffix}`
                    n += 1
                }
                return candidate
            },
            ensureUniqueFieldId: (preferred) => {
                if (!map.has(preferred)) return preferred
                let n = 2
                let candidate = `${preferred}_${n}`
                while (map.has(candidate)) {
                    n += 1
                    candidate = `${preferred}_${n}`
                }
                return candidate
            },
            registerField: async (input) => {
                const created = await createField.mutateAsync({
                    field_id: input.field_id,
                    question_text: input.question_text,
                    input_type: input.input_type,
                    options: input.options,
                    default_section_id: input.default_section_id,
                })
                await fieldsQuery.refetch()
                return toOrgField(created)
            },
        }
    }, [fieldsQuery.data, createField, fieldsQuery])

    const handleSectionsChange = (next: WorkbookSectionsState) => {
        setSections(next)
        setDirty(true)
    }

    const handleSave = async () => {
        if (!sections) return
        try {
            const registryById = new Map(
                (fieldsQuery.data || []).map((row) => [row.field_id, row]),
            )
            for (const draft of Object.values(sections)) {
                if (!draft) continue
                for (const field of draft.fields) {
                    const sourceId = field.source_field_id || field.field_id
                    const existing = registryById.get(sourceId)
                    if (!existing) continue
                    const renamed =
                        field.field_id !== existing.field_id
                            ? field.field_id
                            : undefined
                    const questionChanged =
                        field.question_text !== existing.question_text
                    const typeChanged =
                        field.input_type !== existing.input_type
                    const optionsChanged =
                        JSON.stringify(field.options || []) !==
                        JSON.stringify(existing.options || [])
                    if (
                        !renamed &&
                        !questionChanged &&
                        !typeChanged &&
                        !optionsChanged
                    ) {
                        continue
                    }
                    await updateField.mutateAsync({
                        field_id: existing.field_id,
                        ...(renamed ? { new_field_id: renamed } : {}),
                        ...(questionChanged
                            ? { question_text: field.question_text }
                            : {}),
                        ...(typeChanged
                            ? { input_type: field.input_type }
                            : {}),
                        ...(optionsChanged
                            ? { options: field.options || [] }
                            : {}),
                    })
                    registryById.delete(existing.field_id)
                    registryById.set(field.field_id, {
                        ...existing,
                        field_id: field.field_id,
                        question_text: field.question_text,
                        input_type: field.input_type,
                        options: field.options || [],
                    })
                }
            }

            const definition = sectionsStateToDefinition(sections)
            await saveTemplate.mutateAsync({ definition })
            await fieldsQuery.refetch()
            setDirty(false)
            toast.success('EZOS Applicant Data template saved')
        } catch {
            toast.error('Unable to save EZOS template')
        }
    }

    if (templateQuery.isLoading || fieldsQuery.isLoading) {
        return (
            <Box className={classes.centered}>
                <CircularProgress size={28} />
                <Typography color="textSecondary">Loading template…</Typography>
            </Box>
        )
    }

    if (templateQuery.isError || !templateQuery.data) {
        return (
            <Box className={classes.centered}>
                <Typography color="error">
                    Unable to load the standard Applicant Data template. Confirm
                    you are signed in as an account manager and migrations have
                    run.
                </Typography>
            </Box>
        )
    }

    const template = templateQuery.data

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box>
                    <Typography className={classes.title}>
                        {template.name}
                    </Typography>
                    <Typography className={classes.meta}>
                        Key: {template.key} · Version {template.version}
                        {dirty ? ' · Unsaved changes' : ''}
                    </Typography>
                    {template.description ? (
                        <Typography className={classes.meta}>
                            {template.description}
                        </Typography>
                    ) : null}
                </Box>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!dirty || saveTemplate.isLoading}
                    onClick={() => void handleSave()}
                >
                    {saveTemplate.isLoading ? 'Saving…' : 'Save template'}
                </Button>
            </Box>
            <Box className={classes.body}>
                <Box className={classes.column}>
                    <ApplicantDataTab
                        sections={sections}
                        onSectionsChange={handleSectionsChange}
                        fieldRegistry={registry}
                        editSectionMeta
                        editCatalogFields
                        policyLockedFieldIds={[]}
                    />
                </Box>
            </Box>
        </Box>
    )
}
